import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import Workout from './Workout';
import WorkoutCompletionTimeEditor from './WorkoutCompletionTimeEditor';
import AiSupervisedWorkoutPlayer from '../../features/ai-supervised/AiSupervisedWorkoutPlayer';
import Meal from './Meal';
import Media from './Media';
import Metrics from './Metrics';
import Survey from './Survey';
import analytics from '../../services/AnalyticsService';
import { useGeolocationService } from '../../contexts/GeolocationServiceContext';
import { useForegroundInterval } from '../../services/AppStateService';
import { useVideoService } from '../../contexts/VideoServiceContext';
import { getPendingWorkoutAction } from './activityWorkoutActions';
import TravelReplacementOriginalModal from './TravelReplacementOriginalModal';
import { beginMealUpgrade } from './mealUpgradeFlow';
import { bindCompletionContext, finishWithPosition } from './activityPosition';
import { SUBMIT_NOUNS } from './SubmitFeedback';
import { useWorkoutStart } from './useWorkoutStart';

export const isActivityOngoing = (activity) => {
  return (!activity.due_at || (activity.due_at && new Date(activity.due_at) >= new Date()))
    && activity.status !== "complete"
    && activity.status !== "rest"
    && activity.status !== "replaced";
};

export const isActivityExpired = (activity) => {
  return activity.due_at && new Date(activity.due_at) < new Date()
    && activity.status !== "complete"
    && activity.status !== "rest"
    && activity.status !== "replaced";
};

export const isActivityCompletedOntime = (activity) => {
  return activity.due_at && activity.status === "complete";
};

export const isActivityCancellable = (activity) => {
  return activity.due_at === undefined || activity.due_at === null;
};

export const isActivityEditable = (activity) => {
  return (!activity.due_at || new Date(activity.due_at) >= new Date())
    && activity.status !== 'failed'
    && activity.status !== 'skipped'
    && activity.status !== 'rest'
    && activity.status !== 'replaced';
};

// Self-contained countdown to avoid re-rendering the parent/activity charts
const Countdown = React.memo(function Countdown({ dueAt }) {
  const [label, setLabel] = useState('');

  const compute = useCallback(() => {
    if (!dueAt) {
      setLabel('');
      return;
    }

    const now = Date.now();
    const dueTime = new Date(dueAt).getTime();
    const remaining = dueTime - now;

    if (remaining <= 0) {
      setLabel('Expired');
      return;
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');

    setLabel(`${hh}h ${mm}m ${ss}s`);
  }, [dueAt]);

  useEffect(() => {
    compute();
  }, [compute]);

  useForegroundInterval(compute, dueAt ? 1000 : null);

  if (!label) return null;
  return <div className="countdown">{label}</div>;
});

// Safe formatter for activity.type without using charAt (defensive)
const prettyType = (t) => {
  if (!t) return 'Activity';
  const s = String(t);
  const first = s.length > 0 ? s[0].toUpperCase() : '';
  const rest = s.length > 1 ? s.slice(1) : '';
  return (first + rest) || s;
};

const Activity = ({
  activity: suppliedActivity,
  navigationRequest = null,
  exercises,
  modalService,
  backendService,
  onReady,
  onOpenPlanEditor,
  onActivityComplete,
  onActivityCancel,
  onActivityRefresh,
  onReorderModeChange,
  suppressActions = false,
  compact = false,
  embedded = false,
  readOnly = false,
  recommendationVersion = 0,
  coachImageUrl = null,
  coachEmoji = '🥋',
  coachIdentitySettled = false,
  travelReplacementEnabled = false,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
}) => {
  const [streamNotice, setStreamNotice] = useState(null);
  const videoService = useVideoService();
  const { position, enabled: gymTrackingEnabled } = useGeolocationService() || {};
  const workoutStart = useWorkoutStart({ activity: suppliedActivity, backendService, videoService,
    onActivityRefresh, setStreamNotice, readOnly, suppressActions, position, gymTrackingEnabled });
  const activity = workoutStart.activity;
  const [isEditingName, setIsEditingName] = useState(false);
  const [activityName, setActivityName] = useState(activity.name);
  const [planTitle, setPlanTitle] = useState(null);
  const [isUpgradeAvailable, setIsUpgradeAvailable] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showExpiredDetails, setShowExpiredDetails] = useState(false);
  const [workoutRefreshKey, setWorkoutRefreshKey] = useState(0);
  const [completionPending, setCompletionPending] = useState(false);
  const [completionStage, setCompletionStage] = useState(null);
  const [completionSaved, setCompletionSaved] = useState(false);
  const completionRef = useRef(null);
  const completionScopeRef = useRef(null);
  const completionOwner = backendService.positionOwner;
  const completionIdentity = backendService.authenticatedIdentity;
  useLayoutEffect(() => {
    const scope = new AbortController();
    completionScopeRef.current = scope;
    completionRef.current = null;
    setCompletionPending(false);
    setCompletionStage(null);
    setCompletionSaved(false);
    return () => {
      scope.abort();
      // Reopening can install a successor scope without replacing this effect.
      completionScopeRef.current?.abort();
    };
  }, [backendService, completionOwner, completionIdentity, activity._id, readOnly, suppressActions]);
  const completionStatusRef = useRef(activity.status);
  useLayoutEffect(() => {
    if (completionStatusRef.current === activity.status) return;
    completionStatusRef.current = activity.status;
    // The feed publishes persistence before its refreshes settle. That status
    // change belongs to this operation, not a replacement completion scope.
    if (activity.status === 'complete' && completionSaved && completionRef.current) return;
    completionScopeRef.current?.abort();
    completionScopeRef.current = new AbortController();
    completionRef.current = null;
    setCompletionPending(false);
    setCompletionStage(null);
    setCompletionSaved(false);
  }, [activity.status, completionSaved]);
  const inputRef = useRef(null);
  const reorderModeChangeRef = useRef(onReorderModeChange);
  useEffect(() => { reorderModeChangeRef.current = onReorderModeChange; }, [onReorderModeChange]);
  const handleReorderModeChange = useCallback((active) => reorderModeChangeRef.current?.(active), []);

  const activityPlanId = activity.source?.activity_plan_id;
  const activityPlanEntryId = activity.source?.activity_plan_entry_id;
  const hasSignaledReadyRef = useRef(false);

  useEffect(() => {
    setActivityName(activity.name);
  }, [activity.name]);

  useEffect(() => {
    if (activity.type !== 'workout' || activity.status === 'ongoing') return;
    if (videoService.isBroadcastingWorkout(activity._id)) {
      videoService.stopWorkoutBroadcast(activity._id, { notifyBackend: false }).catch(() => {});
    }
  }, [activity._id, activity.status, activity.type, videoService]);

  useEffect(() => {
    if (!onReady || hasSignaledReadyRef.current) return;
    // For non-workout activities, we can consider them ready immediately.
    if (activity.type !== 'workout') {
      hasSignaledReadyRef.current = true;
      onReady();
    }
  }, [activity.type, onReady]);

  useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    const fetchPlanTitle = async () => {
      if (readOnly || !activityPlanId) {
        setPlanTitle(null);
        return;
      }

      try {
        const planDetails = await backendService.fetchPlanDetails(activityPlanId);
        setPlanTitle(planDetails.title);
      } catch (error) {
        console.error('Error fetching plan details:', error);
      }
    };

    fetchPlanTitle();
  }, [activityPlanId, backendService, readOnly]);

  // Check if an upgrade is available
  useEffect(() => {
    const checkUpgradeAvailability = async () => {
      if (readOnly || !activity || !activity._id) {
        // No id yet (optimistic/new activity) — clear flag and skip backend call
        setIsUpgradeAvailable(false);
        return;
      }
      try {
        const available = await backendService.isUpgradeAvailable(activity._id);
        setIsUpgradeAvailable(available);
      } catch (error) {
        console.error('Error checking upgrade availability:', error);
      }
    };

    checkUpgradeAvailability();

    // Re-check when a plan is updated (e.g. plan editor closed after edits)
    const handlePlanUpdated = () => checkUpgradeAvailability();
    window.addEventListener('regimen:plan:updated', handlePlanUpdated);
    return () => window.removeEventListener('regimen:plan:updated', handlePlanUpdated);
  }, [activity, backendService, readOnly]);

  useEffect(() => {
    // Whenever a different activity or status loads, reset the collapsed view.
    setShowExpiredDetails(false);
  }, [activity._id, activity.status, activity.due_at]);

  const saveActivityName = async () => {
    try {
      await backendService.updateActivityName(activity._id, activityName);
    } catch (error) {
      console.error('Error saving activity name:', error);
      alert('Failed to save activity name.');
    }
  };

  const handleNameBlur = () => {
    setIsEditingName(false);
    saveActivityName();
  };

  const handleNameKeyPress = (event) => {
    if (event.key === 'Enter') {
      setIsEditingName(false);
      saveActivityName();
    }
  };

  const getDisplayName = () => {
    return activityName?.trim() ? activityName : <span className="default-name">Unnamed Activity</span>;
  };

  const getCompletionContext = () => ({ ...bindCompletionContext(backendService), signal: completionScopeRef.current.signal });
  const finishActivity = async (activityId, completionOptions = {}) => {
    if (completionRef.current || completionSaved || readOnly || suppressActions) return;
    if (window.confirm('Are you sure you want to complete this activity?')) {
      const scope = completionScopeRef.current;
      const isCurrent = () => !scope.signal.aborted
        && completionOwner === backendService.positionOwner
        && completionIdentity === backendService.authenticatedIdentity;
      completionRef.current = scope;
      setCompletionPending(true);
      setCompletionStage('checking');
      let persisted = false;
      try {
        const isCompleted = await finishWithPosition(backendService, activityId, navigator.geolocation, {
          ...completionOptions,
          signal: scope.signal,
          onStage: stage => { if (isCurrent()) setCompletionStage(stage); },
          beforeCompletion: activity.type === 'workout' ? () => {
            // Queued set writes are part of the workout being saved.
            if (isCurrent()) setCompletionStage('saving');
            return backendService.waitForWorkoutSetUpdates?.();
          } : undefined,
        });
        if (!isCurrent()) return;
        if (isCompleted) {
          persisted = true;
          if (activity.type === 'workout') workoutStart.complete(isCompleted);
          setCompletionSaved(true);
          // Track analytics based on activity type
          if (activity.type === 'meal') {
            const foods = activity.data?.foods || [];
            const foodCount = foods.length;
            
            // Calculate total macros
            let totalCalories = 0;
            let totalProtein = 0;
            let totalCarbs = 0;
            let totalFat = 0;
            
            foods.forEach(food => {
              const servings = food.serving_count || 1;
              totalCalories += (food.calories || 0) * servings;
              totalProtein += (food.protein || 0) * servings;
              totalCarbs += (food.carbohydrates || 0) * servings;
              totalFat += (food.fat || 0) * servings;
            });
            
            // Determine meal type from activity name or time
            const activityName = activity.name?.toLowerCase() || '';
            let mealType = 'snack'; // default
            if (activityName.includes('breakfast')) mealType = 'breakfast';
            else if (activityName.includes('lunch')) mealType = 'lunch';
            else if (activityName.includes('dinner')) mealType = 'dinner';
            
            // Check if this is the user's first meal (simple localStorage check)
            const isFirstMeal = !localStorage.getItem('first_meal_logged');
            
            analytics.meal.logged(
              mealType,
              Math.round(totalCalories),
              Math.round(totalProtein),
              Math.round(totalCarbs),
              Math.round(totalFat),
              foodCount,
              isFirstMeal
            );
            
            if (isFirstMeal) {
              localStorage.setItem('first_meal_logged', 'true');
            }
          } else if (activity.type === 'workout') {
            try {
              // Fetch full workout data to get exercises and sets
              const exercises = activity.data?.exercises || [];
              
              const exerciseCount = exercises.length;
              const totalSets = exercises.reduce((sum, ex) => sum + (ex.sets?.length || 0), 0);
              
              // Calculate duration
              let durationMin = 0;
              if (activity.started_at && activity.completed_at) {
                const started = new Date(activity.started_at);
                const completed = new Date(activity.completed_at);
                durationMin = Math.round((completed - started) / (1000 * 60));
              }
              
              // Determine workout type from name or default to 'strength'
              const workoutName = activity.name?.toLowerCase() || '';
              let workoutType = 'strength';
              if (workoutName.includes('cardio')) workoutType = 'cardio';
              else if (workoutName.includes('yoga')) workoutType = 'yoga';
              else if (workoutName.includes('hiit')) workoutType = 'hiit';
              
              // Check if this is the user's first workout
              const isFirstWorkout = !localStorage.getItem('first_workout_completed');
              
              analytics.workout.completed(
                activityId,
                workoutType,
                durationMin,
                exerciseCount,
                totalSets,
                isFirstWorkout
              );
              
              if (isFirstWorkout) {
                localStorage.setItem('first_workout_completed', 'true');
              }
            } catch (error) {
              console.error('Error tracking workout analytics:', error);
              // Continue with activity completion even if analytics fails
            }
          }
          
          if (isCurrent()) {
            setCompletionStage('finishing');
            await onActivityComplete(activityId);
          }
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error('Error finishing activity:', error);
        const noun = SUBMIT_NOUNS[activity.type] || 'activity';
        window.alert(persisted
          ? `Your ${noun} ${noun.endsWith('s') ? 'were' : 'was'} saved, but refreshing the view failed. Refresh the page to see it. ${error.message || ''}`
          : `Error finishing activity: ${error.response?.data?.error || error.message || 'Please try again.'}`);
      } finally {
        if (completionRef.current === scope && !scope.signal.aborted) {
          completionRef.current = null;
          setCompletionPending(false);
          setCompletionStage(null);
        }
      }
    }
  };

  const reopenActivity = async (activityId) => {
    if (window.confirm('Are you sure you want to reopen this activity?')) {
      try {
        const openedActivity = await backendService.reopenActivity(activityId);
        if (openedActivity?.type === 'workout'
          && openedActivity.status === 'ongoing'
          && openedActivity.data?.workout_mode !== 'ai_supervised') {
          videoService.startWorkoutBroadcast(activityId)
            .then(result => setStreamNotice(result?.started
              ? { tone: 'live', text: 'Camera live · recording this workout' }
              : null))
            .catch(() => setStreamNotice({ tone: 'error', text: 'Workout reopened, but the camera stream could not start.' }));
        }
        if (openedActivity && onActivityRefresh) {
            onActivityRefresh(openedActivity);
        } else if (openedActivity) {
            // Fallback if no refresh handler, but usually Activity is used in a context where refresh is possible
             window.location.reload();
        }
      } catch (error) {
        console.error('Error reopening activity:', error);
        window.alert(`Error reopening activity: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const cancelActivity = async (activityId) => {
    if (window.confirm('Are you sure you want to cancel this workout? This action cannot be undone.')) {
      try {
        await backendService.deleteActivity(activityId);
        await videoService.stopWorkoutBroadcast(activityId, { notifyBackend: false });
        onActivityCancel(activityId);
      } catch (error) {
        console.error('Error canceling activity:', error);
        window.alert(`Error canceling activity: ${error.response ? error.response.data : error.message}`);
      }
    }
  };

  const upgradeActivity = async (activityId) => {
    if (isUpgrading) return;

    const finishUpgrade = upgradedActivity => {
      setIsUpgradeAvailable(false);
      setWorkoutRefreshKey(prev => prev + 1);
      window.alert(activity.type === 'media' ? 'Media requirements upgraded. Uploaded files were preserved.' : 'Activity upgraded successfully!');
      if (onActivityRefresh) onActivityRefresh(upgradedActivity);
    };

    if (activity.type === 'meal') {
      setIsUpgrading(true);
      try {
        await beginMealUpgrade({
          activityId,
          backendService,
          modalService,
          onUpgraded: finishUpgrade,
        });
      } catch (error) {
        console.error('Error upgrading activity:', error);
        window.alert(`Error upgrading activity: ${error.response?.data?.error || error.message}`);
      } finally {
        setIsUpgrading(false);
      }
      return;
    }

    const confirmation = activity.type === 'media'
      ? 'Updated requirements are available for this media activity. Upgrade its checklist now? Your uploaded files and inference history will be preserved.'
      : 'An upgrade is available for this activity. Do you want to upgrade it?';
    if (window.confirm(confirmation)) {
      setIsUpgrading(true);
      try {
        const upgradedActivity = await backendService.upgradeActivity(activityId);
        finishUpgrade(upgradedActivity);
      } catch (error) {
        console.error('Error upgrading activity:', error);
        window.alert(`Error upgrading activity: ${error.response?.data?.error || error.message}`);
      } finally {
        setIsUpgrading(false);
      }
    }
  };

  const completion = { pending: completionPending, stage: completionStage, saved: completionSaved };
  const isOngoing = isActivityOngoing(activity);
  const pendingWorkoutAction = getPendingWorkoutAction(activity);
  const isPendingWorkout = Boolean(pendingWorkoutAction);
  const isAiSupervisedWorkout = activity.type === 'workout' && activity.data?.workout_mode === 'ai_supervised';
  const shouldShowPendingWorkoutAction = !isAiSupervisedWorkout && isPendingWorkout;
  const pushupsPoseMetadata = (exercises || []).find(exercise => (
    exercise?._id === 'Pushups' || exercise?.id === 'Pushups' || exercise?.name === 'Pushups'
  ))?.pose_detection;

  const isExpired = isActivityExpired(activity);
  const isCancellable = isActivityCancellable(activity);
  const shouldShowFullDetails = embedded || readOnly || !isExpired || showExpiredDetails;
  const isCollapsed = !embedded && !readOnly && isExpired && !showExpiredDetails;
  const dueDateLabel = activity.due_at ? new Date(activity.due_at).toLocaleString() : null;

  const expiredImpact = (() => {
    const dueMs = activity?.due_at ? new Date(activity.due_at).getTime() : NaN;
    const nowMs = Date.now();
    const msPerDay = 24 * 60 * 60 * 1000;

    const affectsWindow = (days) => {
      if (!Number.isFinite(dueMs)) return false;
      // Backend compliance windows are computed in UTC; for a UI hint we treat it as
      // "within the last N days" which is close enough and avoids timezone surprises.
      const oldestIncludedMs = nowMs - (days * msPerDay);
      return dueMs >= oldestIncludedMs;
    };

    const buildExerciseKey = (exercise, idx) => {
      if (!exercise) return `idx:${idx}`;
      if (exercise.exercise_id) return `ex:${exercise.exercise_id}`;
      if (exercise._id) return String(exercise._id);
      if (exercise.name) return `name:${exercise.name}`;
      return `idx:${idx}`;
    };

    const exerciseIsComplete = (exercise) => {
      if (!exercise || !Array.isArray(exercise.sets) || exercise.sets.length === 0) {
        return false;
      }
      return exercise.sets.every((set) => set && set.complete);
    };

    const tallyExercises = (exercises, filterFn = () => true) => {
      const counts = new Map();
      exercises.forEach((ex, idx) => {
        if (!filterFn(ex)) return;
        const key = buildExerciseKey(ex, idx);
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      return counts;
    };

    const plannedItemCount = (() => {
      if (!activity?.due_at) return 0;
      if (activity.type === 'workout') {
        const plannedExercisesRaw = activity?.source?.activity_snapshot?.data?.exercises;
        const plannedExercises = Array.isArray(plannedExercisesRaw) && plannedExercisesRaw.length > 0
          ? plannedExercisesRaw
          : (activity?.data?.exercises || []);
        return plannedExercises.length;
      }
      return 1;
    })();

    const missedIfExpiredNow = (() => {
      if (!activity?.due_at) return 0;
      if (activity?.status === 'complete') return 0;

      if (activity.type !== 'workout') {
        return 1;
      }

      const plannedExercisesRaw = activity?.source?.activity_snapshot?.data?.exercises;
      const plannedExercises = Array.isArray(plannedExercisesRaw) && plannedExercisesRaw.length > 0
        ? plannedExercisesRaw
        : (activity?.data?.exercises || []);
      const actualExercises = activity?.data?.exercises || [];

      const completedCounts = tallyExercises(actualExercises, exerciseIsComplete);
      let missing = 0;
      plannedExercises.forEach((plannedEx, idx) => {
        const key = buildExerciseKey(plannedEx, idx);
        const currentCount = completedCounts.get(key) || 0;
        if (currentCount > 0) {
          completedCounts.set(key, currentCount - 1);
        } else {
          missing += 1;
        }
      });
      return missing;
    })();

    const missedPlanItems = isExpired ? missedIfExpiredNow : 0;
    const earnedPlanItems = activity?.due_at && activity?.status === 'complete' ? plannedItemCount : 0;

    return {
      plannedItemCount,
      earnedPlanItems,
      missedPlanItems,
      missedIfExpiredNow,
      affects: {
        week: affectsWindow(7),
        month: affectsWindow(30),
        year: affectsWindow(365)
      }
    };
  })();

  const impactNote = (() => {
    const hasDue = Boolean(activity?.due_at);
    if (!hasDue) {
      return {
        headline: 'Unscheduled',
        body: "No due date means it doesn't count toward consistency (and won't change your badge)."
      };
    }

    const affects = [];
    if (expiredImpact.affects.week) affects.push('Week');
    if (expiredImpact.affects.month) affects.push('Month');
    if (expiredImpact.affects.year) affects.push('Year');

    const windowSuffix = (() => {
      if (!affects.length) {
        return " It's outside today's badge windows.";
      }
      return ` It will be reflected in your ${affects.join('/')} rate${affects.length === 1 ? '' : 's'} while in-range.`;
    })();

    if (activity?.status === 'complete') {
      const earned = expiredImpact.earnedPlanItems || 0;
      return {
        headline: 'Counted as complete',
        body: `Nice — +${earned} consistency point${earned === 1 ? '' : 's'} for your badge.${windowSuffix}`
      };
    }

    if (activity?.status === 'rest') {
      const reason = activity?.data?.rest_reason;
      const icon = reason === 'sick' ? '🛌' : '🌴';
      return {
        headline: `${icon} Rest day`,
        body: reason === 'sick'
          ? "Resting up. This activity is excluded from your consistency calculation."
          : "Taking a planned break. This activity is excluded from your consistency calculation."
      };
    }

    if (isExpired) {
      const missed = expiredImpact.missedPlanItems || 0;
      return {
        headline: 'Counted as missed',
        body: `${missed} missed consistency point${missed === 1 ? '' : 's'} for your badge.${windowSuffix} Missed items are permanent.`
      };
    }

    // Pending (has due date, not complete, not expired)
    const atRisk = expiredImpact.missedIfExpiredNow || 0;
    const total = expiredImpact.plannedItemCount || 0;
    return {
      headline: 'Not counted yet',
      body: `Completing by the due time will add +${total} consistency point${total === 1 ? '' : 's'}. If missed, it would count as ${atRisk} missed point${atRisk === 1 ? '' : 's'}.${windowSuffix}`
    };
  })();

  const startWorkout = workoutStart.start;

  const handlePlanClick = (e) => {
    e.stopPropagation();
    if (!onOpenPlanEditor || !activityPlanId) return;
    onOpenPlanEditor({ planId: activityPlanId, entryId: activityPlanEntryId });
  };

  const stopWorkout = async (activityId) => {
    if (!window.confirm('Stop this workout and restore your status to not training? Your recorded progress will be kept.')) return;
    try {
      const stoppedActivity = await backendService.stopWorkout(activityId);
      await videoService.stopWorkoutBroadcast(activityId, { notifyBackend: false });
      setStreamNotice(null);
      if (onActivityRefresh) onActivityRefresh(stoppedActivity);
    } catch (error) {
      console.error('Error stopping workout:', error);
      window.alert(`Error stopping workout: ${error.response?.data?.error || error.message}`);
    }
  };

  const openReplacedWorkout = (event) => {
    event?.stopPropagation?.();
    if (activity?.source?.compliance_role !== 'travel_replacement') return;
    modalService.open(TravelReplacementOriginalModal, {
      replacementActivity: activity,
      backendService,
      exercises,
    });
  };

  return (
    <div
      className={`activity-item status-${activity.status} ${isExpired ? 'expired' : ''} ${isCollapsed ? 'collapsed' : ''} ${embedded ? 'embedded' : ''}`}
      key={`${activity._id}-${activity.type}`}
    >
      {workoutStart.notice && (
        <div className="workout-stream-notice is-error" role="alert">
          {workoutStart.notice}
          {workoutStart.recovery && <button type="button" disabled={workoutStart.phase === 'checking'} onClick={workoutStart.check}>
            {workoutStart.phase === 'checking' ? 'Checking…' : activity.status === 'pending' ? 'Check workout status' : 'Refresh workout'}
          </button>}
        </div>
      )}
      {streamNotice && (
        <div className={`workout-stream-notice is-${streamNotice.tone}`} role={streamNotice.tone === 'error' ? 'alert' : 'status'}>
          <span className="workout-stream-notice-dot" aria-hidden="true" />
          {streamNotice.text}
        </div>
      )}
      {/* Hide name when embedded - parent already shows it */}
      {!embedded && (
        <div className="activity-name-parent">
          {isEditingName ? (
            <input
              type="text"
              ref={inputRef}
              value={activityName}
              onChange={(e) => setActivityName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyPress={handleNameKeyPress}
              autoFocus
            />
          ) : (
            <h3 onClick={() => !readOnly && setIsEditingName(true)}>{getDisplayName()}</h3>
          )}
        </div>
      )}
      {!embedded && !readOnly && isExpired && (
        <div className="activity-collapsed-summary">
          <div className="activity-collapsed-copy">
            <p className="activity-collapsed-headline">Missed activity</p>
            {dueDateLabel && (
              <p className="activity-collapsed-due">Was due {dueDateLabel}</p>
            )}
            <p className="activity-collapsed-explainer">
              {impactNote.body}
            </p>
          </div>
          <div className="activity-collapsed-actions">
            <button
              type="button"
              className="activity-toggle-details"
              onClick={() => setShowExpiredDetails((prev) => !prev)}
            >
              {showExpiredDetails ? 'Hide details' : 'Review missed activity'}
            </button>
          </div>
        </div>
      )}
      {!suppressActions && !readOnly && isOngoing && !activity.is_template && (activity.due_at || shouldShowPendingWorkoutAction) && (
        <div className={`activity-collapsed-summary ${compact ? 'compact' : ''}`}>
          {compact ? (
            /* Minimal: just start button if needed, countdown is in todo row */
            shouldShowPendingWorkoutAction && activity.status === 'pending' && (
              <div className="activity-minimal-status centered">
                <button
                  type="button"
                  className="btn-start-minimal"
                  onClick={startWorkout}
                  disabled={workoutStart.disabled}
                  aria-busy={workoutStart.phase === 'starting'}
                >
                  <span aria-live="polite">{workoutStart.phase === 'starting' ? 'Starting…' : pendingWorkoutAction.label}</span>
                </button>
              </div>
            )
          ) : (
            <>
              <div className="activity-collapsed-copy">
                <div className="activity-due-inline">
                  {dueDateLabel && (
                    <span className="due-badge">
                      <span className="due-icon">⏰</span>
                      <span className="due-text">Due {new Date(activity.due_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </span>
                  )}
                  {planTitle && (
                    <button className="plan-badge" type="button" onClick={handlePlanClick}>
                      <span className="plan-icon">📋</span>
                      <span className="plan-text">{planTitle}</span>
                    </button>
                  )}
                </div>
                <Countdown dueAt={activity.due_at} />
                <p className="activity-collapsed-explainer">
                  {impactNote.body}
                </p>
              </div>
              {shouldShowPendingWorkoutAction && (
                <div className="activity-collapsed-actions">
                  {activity.status === 'pending' && (
                    <button
                      type="button"
                      className="btn-select btn-emphasis"
                      onClick={startWorkout}
                      disabled={workoutStart.disabled}
                      aria-busy={workoutStart.phase === 'starting'}
                    >
                      <span aria-live="polite">{workoutStart.phase === 'starting' ? 'Starting…' : pendingWorkoutAction.label}</span>
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {shouldShowFullDetails && (
        <>
          {isAiSupervisedWorkout ? (
            <AiSupervisedWorkoutPlayer
              activity={activity}
              poseMetadata={pushupsPoseMetadata}
              backendService={backendService}
              onActivityRefresh={onActivityRefresh}
              onActivityComplete={onActivityComplete}
              readOnly={readOnly || suppressActions}
              onReady={() => {
                if (onReady && !hasSignaledReadyRef.current) {
                  hasSignaledReadyRef.current = true;
                  onReady();
                }
              }}
            />
          ) : activity.type === 'workout' ? (
            <Workout
              workout={activity}
              navigationRequest={navigationRequest}
              finishActivity={finishActivity}
              completion={completion}
              onWorkoutNameChange={setActivityName}
              onActivityRefresh={onActivityRefresh}
              onReorderModeChange={handleReorderModeChange}
              exercises={exercises}
              modalService={modalService}
              backendService={backendService}
              recommendationVersion={recommendationVersion}
              refreshKey={workoutRefreshKey}
              readOnly={readOnly}
              coachImageUrl={coachImageUrl}
              coachEmoji={coachEmoji}
              coachIdentitySettled={coachIdentitySettled}
              travelReplacementEnabled={travelReplacementEnabled}
              onViewReplacedWorkout={openReplacedWorkout}
              onReady={() => {
                if (onReady && !hasSignaledReadyRef.current) {
                  hasSignaledReadyRef.current = true;
                  onReady();
                }
              }}
            />
          ) : activity.type === 'meal' ? (
            <Meal 
              meal={activity}
              finishActivity={finishActivity}
              completion={completion}
              backendService={backendService} 
              modalService={modalService} 
              onActivityRefresh={onActivityRefresh}
              readOnly={readOnly}
            />
          ) : activity.type === 'media' ? (
            <Media media={activity} finishActivity={finishActivity} completion={completion} backendService={backendService} modalService={modalService} readOnly={readOnly} coachImageUrl={coachImageUrl} coachEmoji={coachEmoji} timezone={timezone} />
          ) : activity.type === 'metric' ? (
            <Metrics metric={activity} finishActivity={finishActivity} completion={completion} getCompletionContext={getCompletionContext} backendService={backendService} modalService={modalService} readOnly={readOnly} />
          ) : activity.type === 'survey' ? (
            <Survey survey={activity} finishActivity={finishActivity} completion={completion} getCompletionContext={getCompletionContext} backendService={backendService} modalService={modalService} readOnly={readOnly} />
          ) : activity.type === 'status_change' ? (
            <div className="status-change-activity">
              <div className="status-change-content">
                <span className="status-change-icon">{
                  { great: '🔥', good: '😊', okay: '😐', tired: '😴', stressed: '😤', recovering: '🩹', sick: '🤒', injured: '🤕' }[activity.data?.status] || '🌡️'
                }</span>
                <span className="status-change-text">Feeling <strong>{activity.data?.status}</strong></span>
              </div>
              {!readOnly && activity.data?.note && (
                <p className="status-change-note">{activity.data.note}</p>
              )}
            </div>
          ) : (
            <p className="activity-name">{activity.name}</p>
          )}
          <WorkoutCompletionTimeEditor
            workout={activity}
            backendService={backendService}
            onUpdated={onActivityRefresh}
            readOnly={readOnly || suppressActions}
            timezone={timezone}
          />
          {!readOnly && !suppressActions && !isExpired && activity.status !== 'complete' && activity.status !== 'rest' && !activity.is_template && (
        <>
          {activity.type === 'workout' && activity.status === 'ongoing' && (
            <div className="center-button pad-button-up-small">
              <button
                className="btn-cancel"
                onClick={() => stopWorkout(activity._id)}
              >
                Stop Workout
              </button>
            </div>
          )}
          {isCancellable && (
            <div className="center-button pad-button-up-small">
              <button
                className="btn-cancel"
                onClick={() => cancelActivity(activity._id)}
              >
                Cancel {prettyType(activity.type)}
              </button>
            </div>
          )}
          {isUpgradeAvailable && (
            <div className="center-button pad-button-up-small">
              <button
                className="btn-upgrade"
                disabled={isUpgrading}
                onClick={() => upgradeActivity(activity._id)}
              >
                {isUpgrading ? 'Upgrading…' : `Upgrade ${prettyType(activity.type)}`}
              </button>
            </div>
          )}
        </>
          )}

          {!readOnly && !suppressActions && (!activity.due_at || new Date(activity.due_at) >= new Date()) && activity.status === 'complete' && !activity.is_template && (
            <div className="center-button pad-button-up-small">
              <button
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#6b7280', 
                  fontSize: '0.8rem', 
                  cursor: 'pointer', 
                  textDecoration: 'none',
                  opacity: 0.7,
                  padding: '4px 8px'
                }}
                onMouseOver={(e) => { e.target.style.opacity = '1'; e.target.style.textDecoration = 'underline'; }}
                onMouseOut={(e) => { e.target.style.opacity = '0.7'; e.target.style.textDecoration = 'none'; }}
                onClick={() => reopenActivity(activity._id)}
              >
                Reopen {prettyType(activity.type)}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Activity;
